import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search,
  Filter,
  Database,
  CheckCircle2,
  Clock,
  Tag,
  ChevronDown,
  X,
  AlertCircle,
  Info,
  Plus
} from 'lucide-react'
import { apiClient } from '@/lib/api'

interface DatabaseProblem {
  id: string
  title: string
  description: string
  difficulty: 'easy' | 'medium' | 'hard'
  challenge_type: string
  database_type: string
  time_limit_minutes: number
  is_active: boolean
}

interface DatabaseProblemsSelectorProps {
  selectedProblems: string[]
  onProblemsChange: (problemIds: string[]) => void
  maxProblems?: number
  allowedDifficulties?: string[]
  className?: string
}

export default function DatabaseProblemsSelector({
  selectedProblems,
  onProblemsChange,
  maxProblems = 5,
  allowedDifficulties = ['easy', 'medium', 'hard'],
  className = ''
}: DatabaseProblemsSelectorProps) {
  const [problems, setProblems] = useState<DatabaseProblem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('')
  const [selectedType, setSelectedType] = useState<string>('')
  const [showFilters, setShowFilters] = useState(false)

  const loadProblems = async () => {
    try {
      setLoading(true)
      setError(null)

      const params: Record<string, string | number> = { limit: 50 }
      if (selectedDifficulty) params.difficulty = selectedDifficulty
      if (selectedType) params.challenge_type = selectedType

      const response = await apiClient.getDatabaseChallenges(params)

      if (response.success && response.data) {
        // Convert database challenges to match the expected format
        const formattedProblems = (response.data as unknown as any[]).map((challenge: any) => ({
          id: challenge.uuid,
          title: challenge.title,
          description: challenge.description,
          difficulty: challenge.difficulty?.toLowerCase() || 'medium',
          challenge_type: challenge.challenge_type,
          database_type: challenge.database_type,
          time_limit_minutes: challenge.time_limit_minutes || 60,
          is_active: true
        }))

        setProblems(formattedProblems)
      } else {
        setProblems([])
        setError('Failed to load database problems')
      }
    } catch (err) {
      setError('Failed to load database problems')
      console.error('Error loading database problems:', err)
      setProblems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadProblems()
  }, [selectedDifficulty, selectedType])

  const filteredProblems = problems.filter(problem => {
    const matchesSearch = !searchQuery || 
      problem.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      problem.description.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesDifficulty = allowedDifficulties.includes(problem.difficulty)
    
    return matchesSearch && matchesDifficulty
  })

  const handleProblemToggle = (problemId: string) => {
    const currentSelection = selectedProblems || []
    if (currentSelection.includes(problemId)) {
      onProblemsChange(currentSelection.filter(id => id !== problemId))
    } else if (currentSelection.length < maxProblems) {
      onProblemsChange([...currentSelection, problemId])
    }
  }

  const toggleProblem = (problemId: string) => {
    handleProblemToggle(problemId)
  }

  const selectedProblemsData = (problems || []).filter(p => selectedProblems.includes(p.id))
  const totalTimeEstimate = selectedProblemsData.reduce((sum, p) => sum + (p.time_limit_minutes || 60), 0)

  const clearSelection = () => {
    onProblemsChange([])
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'hard': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'query': return 'text-blue-600 bg-blue-50'
      case 'schema_design': return 'text-purple-600 bg-purple-50'
      case 'optimization': return 'text-orange-600 bg-orange-50'
      case 'analysis': return 'text-green-600 bg-green-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Database className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900">Database Questions</h3>
          <span className="text-sm text-gray-500">
            ({(selectedProblems || []).length}/{maxProblems} selected)
          </span>
        </div>
        
        {(selectedProblems || []).length > 0 && (
          <button
            onClick={clearSelection}
            className="text-sm text-red-600 hover:text-red-800 flex items-center space-x-1"
          >
            <X className="h-4 w-4" />
            <span>Clear all</span>
          </button>
        )}
      </div>

      {/* Selected Problems Summary */}
      {selectedProblems.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-blue-900">
              Selected Problems ({selectedProblems.length}/{maxProblems})
            </h4>
            <div className="text-sm text-blue-700">
              Estimated time: {totalTimeEstimate} minutes
            </div>
          </div>
          <div className="space-y-2">
            {selectedProblemsData.map(problem => (
              <div key={problem.id} className="flex items-center justify-between bg-white rounded px-3 py-2">
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(problem.difficulty)}`}>
                    {problem.difficulty.toUpperCase()}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{problem.title}</span>
                  <span className="text-xs text-gray-500">{problem.challenge_type}</span>
                </div>
                <button
                  onClick={() => toggleProblem(problem.id)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search database problems..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            <span className="text-sm">Filters</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>

          {(selectedDifficulty || selectedType) && (
            <div className="flex items-center space-x-2">
              {selectedDifficulty && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {selectedDifficulty}
                  <button
                    onClick={() => setSelectedDifficulty('')}
                    className="ml-1 h-3 w-3 hover:bg-blue-200 rounded-full flex items-center justify-center"
                  >
                    <X className="h-2 w-2" />
                  </button>
                </span>
              )}
              {selectedType && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {selectedType}
                  <button
                    onClick={() => setSelectedType('')}
                    className="ml-1 h-3 w-3 hover:bg-green-200 rounded-full flex items-center justify-center"
                  >
                    <X className="h-2 w-2" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>

        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
                <select
                  value={selectedDifficulty}
                  onChange={(e) => setSelectedDifficulty(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All difficulties</option>
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Challenge Type</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All types</option>
                  <option value="query">Query Writing</option>
                  <option value="schema_design">Schema Design</option>
                  <option value="optimization">Optimization</option>
                  <option value="analysis">Data Analysis</option>
                </select>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <span className="text-sm text-red-700">{error}</span>
          <button
            onClick={loadProblems}
            className="ml-auto text-sm text-red-600 hover:text-red-800 underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading database problems...</span>
        </div>
      )}

      {/* Problems List */}
      {!loading && !error && (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredProblems.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Database className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No database problems found</p>
              {searchQuery && (
                <p className="text-sm mt-2">Try adjusting your search or filters</p>
              )}
            </div>
          ) : (
            filteredProblems.map((problem) => {
              const currentSelection = selectedProblems || []
              const isSelected = currentSelection.includes(problem.id)
              const canSelect = !isSelected && currentSelection.length < maxProblems

              return (
                <motion.div
                  key={problem.id}
                  layout
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : canSelect
                      ? 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                  }`}
                  onClick={() => canSelect || isSelected ? handleProblemToggle(problem.id) : null}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {isSelected ? (
                          <CheckCircle2 className="h-5 w-5 text-blue-600" />
                        ) : (
                          <div className="h-5 w-5 border-2 border-gray-300 rounded"></div>
                        )}
                        <h4 className="text-lg font-medium text-gray-900">{problem.title}</h4>
                      </div>

                      <p className="text-gray-600 mb-3 ml-8">{problem.description}</p>

                      <div className="flex items-center space-x-3 ml-8">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(problem.difficulty)}`}>
                          {problem.difficulty}
                        </span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(problem.challenge_type)}`}>
                          {problem.challenge_type}
                        </span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-gray-600 bg-gray-100">
                          {problem.database_type}
                        </span>
                        <div className="flex items-center text-sm text-gray-500">
                          <Clock className="h-4 w-4 mr-1" />
                          {problem.time_limit_minutes} min
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
